// src/pages/account/admin/videos/AdminVideos.js
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  where,
} from "firebase/firestore";
import { Card, Button, Badge, Row, Col, Form, Spinner, Alert } from "react-bootstrap";

import { db } from "../../../../shared/libs/firebaseConfig";
import { useAuth } from "../../../../shared/auth/AuthProvider";

const PAGE_SIZE = 15;

function normalizeText(s = "") {
  return String(s || "").toLowerCase().trim();
}

export default function AdminVideos() {
  const navigate = useNavigate();
  const { user, userDoc, loading } = useAuth();

  const [items, setItems] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [err, setErr] = useState("");

  const [qText, setQText] = useState("");
  const [status, setStatus] = useState("all"); // all | active | hidden
  const [kind, setKind] = useState("all");     // all | youtube | podcast | facebook | vertical
  const [ownerSearch, setOwnerSearch] = useState(""); // ค้นด้วย ownerUid (หรือ name ถ้าเก็บ)

  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(true);

  const isAdmin = useMemo(() => {
    const roles = userDoc?.role;
    const arr = Array.isArray(roles) ? roles : [roles].filter(Boolean);
    const set = new Set(arr.map((x) => String(x).toLowerCase()));
    return set.has("admin");
  }, [userDoc]);

  const buildBaseQuery = () => {
    const parts = [orderBy("createdAt", "desc"), limit(PAGE_SIZE)];

    // filter status
    if (status === "active") parts.unshift(where("isActive", "==", true));
    if (status === "hidden") parts.unshift(where("isActive", "==", false));

    // filter kind
    if (kind !== "all") parts.unshift(where("kind", "==", kind));

    // filter ownerUid (exact match เท่านั้น เพราะ Firestore ไม่เหมาะกับ contains)
    const o = ownerSearch.trim();
    if (o) parts.unshift(where("ownerUid", "==", o));

    return query(collection(db, "videos"), ...parts);
  };

  const loadFirst = async () => {
    if (!user?.uid) return;
    setErr("");
    setLoadingList(true);
    setHasMore(true);
    setCursor(null);

    try {
      const q = buildBaseQuery();
      const snap = await getDocs(q);
      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setItems(rows);
      setCursor(snap.docs[snap.docs.length - 1] || null);
      setHasMore(snap.docs.length === PAGE_SIZE);
      setLoadingList(false);
    } catch (e) {
      console.error(e);
      setErr(e?.message || "โหลดรายการวิดีโอไม่สำเร็จ");
      setLoadingList(false);
    }
  };

  const loadMore = async () => {
    if (!cursor || !hasMore) return;
    setErr("");
    setLoadingMore(true);

    try {
      const parts = [];

      if (status === "active") parts.push(where("isActive", "==", true));
      if (status === "hidden") parts.push(where("isActive", "==", false));
      if (kind !== "all") parts.push(where("kind", "==", kind));

      const o = ownerSearch.trim();
      if (o) parts.push(where("ownerUid", "==", o));

      parts.push(orderBy("createdAt", "desc"));
      parts.push(startAfter(cursor));
      parts.push(limit(PAGE_SIZE));

      const q = query(collection(db, "videos"), ...parts);
      const snap = await getDocs(q);
      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

      setItems((prev) => [...prev, ...rows]);
      setCursor(snap.docs[snap.docs.length - 1] || cursor);
      setHasMore(snap.docs.length === PAGE_SIZE);
      setLoadingMore(false);
    } catch (e) {
      console.error(e);
      setErr(e?.message || "โหลดเพิ่มไม่สำเร็จ");
      setLoadingMore(false);
    }
  };

  // โหลดครั้งแรก + reload เมื่อ filter เปลี่ยน
  useEffect(() => {
    if (!user?.uid) return;
    loadFirst();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid, status, kind, ownerSearch]);

  // search client-side (เฉพาะชุดที่โหลดมาแล้ว)
  const filteredByText = useMemo(() => {
    const t = normalizeText(qText);
    if (!t) return items;
    return items.filter((it) => {
      const title = normalizeText(it.title);
      const url = normalizeText(it.url);
      const cats = Array.isArray(it.categories) ? it.categories.join(" ") : "";
      const ownerUid = normalizeText(it.ownerUid);
      const ownerName = normalizeText(it?.createdBy?.name || "");
      return (
        title.includes(t) ||
        url.includes(t) ||
        normalizeText(cats).includes(t) ||
        normalizeText(it.kind).includes(t) ||
        ownerUid.includes(t) ||
        ownerName.includes(t)
      );
    });
  }, [items, qText]);

  if (loading) return null;
  if (!user) return null;
  if (!isAdmin) return <Alert variant="warning">หน้านี้สำหรับ Admin เท่านั้น</Alert>;

  return (
    <div>
      <div className="d-flex align-items-start justify-content-between gap-3 mb-3">
        <div>
          <h4 className="mb-0">วิดีโอในระบบ</h4>
          <div className="text-muted small">
            แสดงรายการ <code>videos ทั้งหมด</code>
          </div>
        </div>

        <div className="d-flex gap-2">
          <Button variant="outline-secondary" onClick={loadFirst} disabled={loadingList || loadingMore}>
            รีเฟรช
          </Button>
          <Button variant="primary" onClick={() => navigate("/account/creator/videos/new")}>
            + เพิ่มวิดีโอ
          </Button>
        </div>
      </div>

      {err && <Alert variant="danger">{err}</Alert>}

      {/* Filters */}
      <Card className="border mb-3">
        <Card.Body>
          <Row className="g-2 align-items-end">
            <Col md={4}>
              <Form.Label>ค้นหา (client-side)</Form.Label>
              <Form.Control
                value={qText}
                onChange={(e) => setQText(e.target.value)}
                placeholder="ชื่อ, url, หมวด, kind, ownerUid, ownerName..."
              />
            </Col>

            <Col md={4}>
              <Form.Label>สถานะ</Form.Label>
              <Form.Select value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="all">ทั้งหมด</option>
                <option value="active">Active</option>
                <option value="hidden">Hidden</option>
              </Form.Select>
            </Col>

            <Col md={4}>
              <Form.Label>ประเภท</Form.Label>
              <Form.Select value={kind} onChange={(e) => setKind(e.target.value)}>
                <option value="all">ทั้งหมด</option>
                <option value="youtube">youtube</option>
                <option value="podcast">podcast</option>
                <option value="facebook">facebook</option>
                <option value="vertical">vertical</option>
              </Form.Select>
            </Col>

            {/* <Col md={3}>
              <Form.Label>กรอง ownerUid (exact)</Form.Label>
              <Form.Control
                value={ownerSearch}
                onChange={(e) => setOwnerSearch(e.target.value)}
                placeholder="ใส่ uid เจ้าของคอร์ส/วิดีโอ"
              />
            </Col> */}

            {/* <Col md={1} className="d-grid">
              <Button
                variant="outline-secondary"
                onClick={() => {
                  setQText("");
                  setStatus("all");
                  setKind("all");
                  setOwnerSearch("");
                }}
              >
                ล้าง
              </Button>
            </Col> */}
          </Row>

          {/* <div className="text-muted small mt-2">
            * “กรอง ownerUid” เป็นแบบ exact match เพื่อให้ query ได้จริงบน Firestore
          </div> */}
        </Card.Body>
      </Card>

      {/* List */}
      {loadingList ? (
        <div className="py-5 text-center text-muted">
          <Spinner animation="border" size="sm" /> กำลังโหลดรายการ…
        </div>
      ) : filteredByText.length === 0 ? (
        <Card className="border">
          <Card.Body className="text-center py-5">
            <div className="mb-2 fw-semibold">ไม่พบรายการ</div>
            <div className="text-muted mb-3">ลองปรับ filter หรือกดล้างตัวกรอง</div>
            <Button
              variant="outline-secondary"
              onClick={() => {
                setQText("");
                setStatus("all");
                setKind("all");
                setOwnerSearch("");
              }}
            >
              ล้างตัวกรอง
            </Button>
          </Card.Body>
        </Card>
      ) : (
        <>
          <Row className="g-3">
            {filteredByText.map((v) => (
              <Col key={v.id} xs={12} md={6} lg={4}>
                <Card className="border h-100">
                  {/* thumb */}
                  <div
                    style={{
                      width: "100%",
                      aspectRatio: "16/9",
                      background: "rgba(127,127,127,.08)",
                      borderTopLeftRadius: 6,
                      borderTopRightRadius: 6,
                      overflow: "hidden",
                    }}
                  >
                    {v.thumbUrl ? (
                      <img
                        src={v.thumbUrl}
                        alt={v.title || "thumb"}
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        onError={(e) => (e.currentTarget.style.display = "none")}
                      />
                    ) : (
                      <div className="h-100 d-flex align-items-center justify-content-center text-muted small">
                        ไม่มีรูปตัวอย่าง
                      </div>
                    )}
                  </div>

                  <Card.Body className="d-flex flex-column">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <Badge bg={v.isActive ? "success" : "secondary"}>
                        {v.isActive ? "Active" : "Hidden"}
                      </Badge>
                      <Badge bg="light" text="dark" style={{ border: "1px solid rgba(0,0,0,.12)" }}>
                        {v.kind || "video"}
                      </Badge>
                    </div>

                    <div className="fw-semibold" style={{ lineHeight: 1.25 }}>
                      {v.title || "(ไม่มีชื่อ)"}
                    </div>

                   {/*  <div className="text-muted small mt-2" style={{ wordBreak: "break-all" }}>
                      {v.url ? v.url : v.fileUrl ? v.fileUrl : v.storagePath ? v.storagePath : ""}
                    </div> */}

                    <div className="mt-2 text-muted small">
                      {/* Owner: <code>{v.ownerUid || "-"}</code> */}
                      {!!v?.createdBy?.name && (
                        <>
                          
                          <span className="">{v.createdBy.name}</span>
                        </>
                      )}
                    </div>

                    {Array.isArray(v.categories) && v.categories.length > 0 && (
                      <div className="d-flex flex-wrap gap-1 mt-3">
                        {v.categories.slice(0, 4).map((c) => (
                          <Badge
                            key={c}
                            bg="light"
                            text="dark"
                            style={{ border: "1px solid rgba(0,0,0,.12)" }}
                          >
                            {c}
                          </Badge>
                        ))}
                        {v.categories.length > 4 && (
                          <Badge bg="light" text="dark" style={{ border: "1px solid rgba(0,0,0,.12)" }}>
                            +{v.categories.length - 4}
                          </Badge>
                        )}
                      </div>
                    )}

                    <div className="mt-auto pt-3 d-flex gap-2">
                      {/* ✅ admin เปิดแก้ไขได้: ใช้หน้า editor เดียวกัน */}
                      <Button
                        as={Link}
                        to={`/account/creator/videos/${v.id}`}
                        variant="outline-primary"
                        className="w-100"
                      >
                        แก้ไข
                      </Button>
                      {/* <Button
                        variant="primary"
                        className="w-100"
                        onClick={() => navigate(`/account/creator/videos/${v.id}`)}
                      >
                        เปิด
                      </Button> */}
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>

          {/* Load more */}
          <div className="d-flex justify-content-center mt-4">
            {hasMore ? (
              <Button variant="outline-secondary" onClick={loadMore} disabled={loadingMore}>
                {loadingMore ? "กำลังโหลด..." : "โหลดเพิ่ม"}
              </Button>
            ) : (
              <div className="text-muted small">แสดงครบแล้ว</div>
            )}
          </div>

          {/* <div className="text-muted small mt-3">
            * ช่องค้นหาเป็น client-side ในชุดข้อมูลที่โหลดมาแล้ว (ถ้าต้องการค้นหาทั้งระบบแบบ contains ค่อยทำ search index ภายหลัง)
          </div> */}
        </>
      )}
    </div>
  );
}